import { pontoNoMundo } from './ancoragem'
import { parametrosDoObjeto } from './parametros'
import { analisarTalabartes, entradaDeChoque } from './quedaEmAltura'
import { calcularForcaDeChoque, curvaDeForca, curvaNoInstante, type CurvaDeForca, type ResultadoForcaDeChoque } from './forcaDeChoque'
import { apoioNoPonto } from './superficies'
import { criarDetectorDeImpacto, pontosDoCorpo, type CorpoDaQueda, type ParteDoCorpo } from './impactoQueda'
import { GRAVIDADE_M_S2 } from './estatica'
import type { CenaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * Queda animada e pêndulo, DETERMINÍSTICOS e sem motor de física: a mesma
 * montagem dá sempre os mesmos quadros. É um modelo didático em quatro tempos:
 *
 *  1. saída: o trabalhador perde o apoio pela borda, no sentido oposto à
 *     ancoragem, até onde o talabarte deixa;
 *  2. queda livre na vertical (g = 9,80665 m/s²) até o talabarte esticar;
 *  3. frenagem: o corpo segue descendo na vertical enquanto o absorvedor abre
 *     `A` metros (o talabarte vai de `L` a `L + A`), com desaceleração
 *     constante, e o engate para no ponto de parada (raio `L + A`);
 *  4. pêndulo: parte do repouso no ponto de parada e balança em volta da
 *     ancoragem, no plano vertical que a contém, amortecido, até ficar parado
 *     na vertical da ancoragem (engate `L + A` abaixo dela).
 *
 * O corpo é um segmento vertical (pés `E` abaixo do engate, cabeça 1,75 m acima
 * dos pés) que só se translada; a conferência analítica é a altura final dos
 * pés, `ancoragem − L − d − m·g/k − E`. A zona livre de queda usa a abertura
 * máxima do absorvedor, então costuma sobrar margem. A primeira
 * parte do corpo que entra no terreno (chão ou parede da laje) ou atravessa o
 * piso de uma torre (um patamar, a plataforma do topo) encerra a animação com
 * um impacto. Quem está num patamar está apoiado nele e cai pelo alçapão ou
 * pela lateral. Os montantes e as diagonais da torre não bloqueiam o balanço. Os tempos da frenagem e do amortecimento são
 * didáticos, não medidas.
 */

export const QUADROS_POR_SEGUNDO_QUEDA = 30
const PASSO_S = 1 / QUADROS_POR_SEGUNDO_QUEDA
const PASSO_SAIDA_M = 0.02
/** Folga que o trabalhador dá para fora da borda ao sair, em metros. */
const FOLGA_NA_BORDA_M = 0.1
/** Quanto tempo o talabarte leva para voltar do alongamento do pico ao de quem está pendurado (didático). */
const RETORNO_DO_ALONGAMENTO_S = 0.15
/** Fração do amortecimento crítico do pêndulo (didática: cerca de três oscilações até parar). */
const AMORTECIMENTO_PENDULO = 0.25
const DURACAO_MAXIMA_PENDULO_S = 15
const PARADO_GRAUS = 0.3

export type FaseQueda = 'queda-livre' | 'frenagem' | 'pendulo' | 'parado' | 'rompido'
export type { ParteDoCorpo }

export interface RompimentoQueda {
  readonly t: number
  readonly forcaKN: number
  readonly quadro: number
  readonly alturaM?: number
  readonly cargaRupturaKN?: number
  readonly velocidadeImpactoMs?: number
}

export interface QuadroQueda {
  readonly t: number
  readonly fase: FaseQueda
  readonly engate: VetorTabletop
  readonly pes: VetorTabletop
  /** Força no talabarte neste quadro: zero na queda livre, a curva na frenagem, o peso depois. */
  readonly forcaKN: number
}

export interface ImpactoQueda {
  /** Índice do último quadro (o do impacto). */
  readonly quadro: number
  readonly t: number
  readonly ponto: VetorTabletop
  readonly parteDoCorpo: ParteDoCorpo
  /** Nome da peça de terreno atingida. */
  readonly alvo: string
  readonly velocidadeMs: number
  readonly fase: FaseQueda
}

export type SituacaoQueda =
  /** A queda foi calculada. */
  | 'ok'
  /** O talabarte segura o trabalhador na superfície antes de ele sair do apoio: não há queda. */
  | 'apoiado'
  /** O talabarte é curto para a distância até a ancoragem: o trabalhador não está preso. */
  | 'nao-alcanca'

export interface ResultadoQuedaAnimada {
  readonly idCorda: string
  readonly idTrabalhador: string
  /** A ancoragem do talabarte, no mundo. */
  readonly pontoAncoragem: VetorTabletop
  /** Giro do trabalhador em Y, para desenhar o fantasma virado como ele. */
  readonly guinadaRad: number
  readonly situacao: SituacaoQueda
  readonly quadros: readonly QuadroQueda[]
  /** Engate no instante em que perde o apoio. */
  readonly pontoDeSaida: VetorTabletop
  /** Engate no instante em que o talabarte estica. */
  readonly pontoTenso: VetorTabletop
  /** Engate no ponto de parada, depois da frenagem. */
  readonly pontoDeParada: VetorTabletop
  /** Queda livre real, a partir do ponto de saída (com o desvio lateral). */
  readonly quedaLivreM: number
  readonly velocidadeAoEsticarMs: number
  /** Ângulo do pêndulo em relação à vertical, no ponto de parada. */
  readonly anguloDoPenduloGraus: number
  readonly impacto: ImpactoQueda | null
  /** Informação de ruptura mecânica do talabarte, se ocorreu. */
  readonly rompimento?: RompimentoQueda | null
  /** Altura final dos pés quando a queda termina sem impacto (a conferência analítica). */
  readonly pesFinaisY: number | null
  readonly duracaoS: number
  /** A força de choque desta queda (com a queda livre real), e a curva dela no tempo. */
  readonly choque: ResultadoForcaDeChoque | null
  readonly curvaDeForca: CurvaDeForca | null
  /** Instante, na régua da animação, em que o talabarte estica e a curva começa. */
  readonly inicioDaFrenagemS: number
  /** Como a queda foi feita: o cálculo animado (padrão) ou a simulação com o motor de física. */
  readonly origem: OrigemDaQueda
  /** O que a simulação com física mediu; `null` na queda calculada (o que o cálculo diz está em `choque`). */
  readonly simulado: MedidasSimuladas | null
}

export type OrigemDaQueda = 'calculo' | 'fisica'

export interface MedidasSimuladas {
  /** O maior pico de força na corda, em kN. */
  readonly picoKN: number
  /** Quanto o redutor escorregou, em metros. */
  readonly aberturaUsadaM: number
  /** Do talabarte esticar ao ponto mais baixo da frenagem: mola e redutor juntos. */
  readonly descidaAposEsticarM: number
  readonly alongamentoNoPicoM: number
}

const dist = (a: VetorTabletop, b: VetorTabletop) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
const distHorizontal = (a: VetorTabletop, b: VetorTabletop) => Math.hypot(a[0] - b[0], a[2] - b[2])

type Corpo = CorpoDaQueda

const DIRECOES_DE_SAIDA = 24

/**
 * Acha o ponto em que o trabalhador perde o apoio: anda na horizontal, na
 * direção em que a borda está mais perto, até o terreno cair abaixo dos pés. O
 * talabarte limita até onde ele vai. A busca começa pelo sentido oposto ao da
 * ancoragem (o que fica na frente se ele já estiver no meio dela) e só troca de
 * direção por uma estritamente mais curta, então o resultado não oscila.
 * `null` quando o talabarte o segura antes de ele sair do apoio.
 */
function pontoDeSaidaDoApoio(
  cena: CenaTabletop,
  engate: VetorTabletop,
  yPes: number,
  ancoragem: VetorTabletop,
  L: number,
  direcaoFallback: readonly [number, number],
): VetorTabletop | null {
  // sem apoio: no nível dos pés não há chão nem piso de torre (o alçapão, a borda da laje)
  const semApoio = (p: VetorTabletop) => !apoioNoPonto(cena, p[0], p[2], yPes).apoiado
  if (semApoio(engate)) return engate
  let ux = engate[0] - ancoragem[0]
  let uz = engate[2] - ancoragem[2]
  const n = Math.hypot(ux, uz)
  if (n < 0.05) [ux, uz] = direcaoFallback
  else [ux, uz] = [ux / n, uz / n]
  const anguloBase = Math.atan2(uz, ux)

  let melhor: { s: number; ux: number; uz: number } | null = null
  for (let k = 0; k < DIRECOES_DE_SAIDA; k += 1) {
    const angulo = anguloBase + (2 * Math.PI * k) / DIRECOES_DE_SAIDA
    const dx = Math.cos(angulo)
    const dz = Math.sin(angulo)
    for (let s = PASSO_SAIDA_M; s <= L + 1e-9; s += PASSO_SAIDA_M) {
      if (melhor && s >= melhor.s) break
      const p: VetorTabletop = [engate[0] + dx * s, engate[1], engate[2] + dz * s]
      if (dist(p, ancoragem) > L) break
      if (semApoio(p)) {
        melhor = { s, ux: dx, uz: dz }
        break
      }
    }
  }
  if (!melhor) return null
  const alem: VetorTabletop = [
    engate[0] + melhor.ux * (melhor.s + FOLGA_NA_BORDA_M),
    engate[1],
    engate[2] + melhor.uz * (melhor.s + FOLGA_NA_BORDA_M),
  ]
  const naBorda: VetorTabletop = [engate[0] + melhor.ux * melhor.s, engate[1], engate[2] + melhor.uz * melhor.s]
  return dist(alem, ancoragem) <= L ? alem : naBorda
}

/**
 * A queda livre geométrica: do ponto de saída, na vertical, até o talabarte
 * esticar. `dh` é o desvio horizontal até a ancoragem; `tenso`, o engate no
 * instante em que a corda fica reta.
 */
export function quedaLivreAteEsticar(saida: VetorTabletop, ancoragem: VetorTabletop, comprimentoM: number): { dh: number; hLivre: number; tenso: VetorTabletop } {
  const dh = distHorizontal(saida, ancoragem)
  const folga = Math.sqrt(Math.max(0, comprimentoM * comprimentoM - dh * dh))
  const hLivre = Math.max(0, saida[1] - (ancoragem[1] - folga))
  return { dh, hLivre, tenso: [saida[0], saida[1] - hLivre, saida[2]] }
}

export type BaseDoResultado = Omit<ResultadoQuedaAnimada, 'situacao' | 'quadros' | 'pontoDeSaida' | 'pontoTenso' | 'pontoDeParada'>

/** O que a queda calculada e a simulada têm em comum: o talabarte analisado, o trabalhador e de onde ele sai. */
export interface PreparoQueda {
  readonly analise: ReturnType<typeof analisarTalabartes>[number]
  readonly trabalhador: ObjetoTabletop
  readonly comprimentoM: number
  readonly corpo: CorpoDaQueda
  readonly ancoragem: VetorTabletop
  readonly base: BaseDoResultado
  /** `ok` quando há queda; nos outros casos, `semQueda` já é o resultado a devolver. */
  readonly situacao: SituacaoQueda
  /** O engate no instante em que perde o apoio, em repouso; `null` quando não há queda. */
  readonly saida: VetorTabletop | null
  readonly semQueda: ResultadoQuedaAnimada | null
}

/**
 * Acha a corda, o trabalhador, a ancoragem e o ponto em que ele perde o apoio.
 * `null` se a corda não é um talabarte de um trabalhador, ou se a ancoragem não
 * existe mais.
 */
export function prepararQueda(cena: CenaTabletop, idCorda: string, origem: OrigemDaQueda = 'calculo'): PreparoQueda | null {
  const analise = analisarTalabartes(cena).find((t) => t.idCorda === idCorda)
  if (!analise) return null
  const trabalhador = cena.objetos.find((o) => o.id === analise.idTrabalhador) as ObjetoTabletop
  const p = parametrosDoObjeto(trabalhador)
  const L = p.comprimentoTalabarteM!
  const corpo: Corpo = { engateAosPesM: p.engateAosPesM! }
  const A = analise.pontoAncoragem
  const engate0 = pontoNoMundo(trabalhador, 'engate-dorsal')
  const yPes0 = trabalhador.transformacao.posicao[1]

  // sentido para onde o trabalhador olha, se ele estiver bem debaixo da ancoragem
  const guinada = trabalhador.transformacao.rotacao[1]
  const frente: readonly [number, number] = [Math.sin(guinada), Math.cos(guinada)]

  const base: BaseDoResultado = {
    idCorda,
    idTrabalhador: trabalhador.id,
    pontoAncoragem: A,
    guinadaRad: guinada,
    anguloDoPenduloGraus: 0,
    impacto: null,
    rompimento: null,
    pesFinaisY: null,
    duracaoS: 0,
    quedaLivreM: 0,
    velocidadeAoEsticarMs: 0,
    choque: null,
    curvaDeForca: null,
    inicioDaFrenagemS: 0,
    origem,
    simulado: null,
  }
  const semQueda = (situacao: 'nao-alcanca' | 'apoiado'): PreparoQueda => ({
    analise,
    trabalhador,
    comprimentoM: L,
    corpo,
    ancoragem: A,
    base,
    situacao,
    saida: null,
    semQueda: { ...base, situacao, quadros: [], pontoDeSaida: engate0, pontoTenso: engate0, pontoDeParada: engate0 },
  })

  // o talabarte não chega: o trabalhador não está preso, não há o que animar
  if (!analise.resultado.alcanca) return semQueda('nao-alcanca')
  const saida = pontoDeSaidaDoApoio(cena, engate0, yPes0, A, L, frente)
  if (!saida) return semQueda('apoiado')
  return { analise, trabalhador, comprimentoM: L, corpo, ancoragem: A, base, situacao: 'ok', saida, semQueda: null }
}

/**
 * A queda de um trabalhador, quadro a quadro. `null` se a corda não é um
 * talabarte de um trabalhador, ou se a ancoragem não existe mais.
 */
export function simularQuedaAnimada(cena: CenaTabletop, idCorda: string): ResultadoQuedaAnimada | null {
  const preparo = prepararQueda(cena, idCorda)
  if (!preparo) return null
  if (preparo.semQueda) return preparo.semQueda
  const { trabalhador, comprimentoM: L, corpo, ancoragem: A, base } = preparo
  const saida = preparo.saida!

  // --- 2. queda livre até o talabarte esticar
  const { dh, hLivre, tenso } = quedaLivreAteEsticar(saida, A, L)
  const vTenso = Math.sqrt(2 * GRAVIDADE_M_S2 * hLivre)
  const tLivre = Math.sqrt((2 * hLivre) / GRAVIDADE_M_S2)

  // --- 3. frenagem na vertical: o corpo continua descendo enquanto o talabarte
  // estica e o absorvedor abre, pela curva da força de choque; a horizontal não muda
  const entrada = entradaDeChoque(trabalhador, hLivre)
  const choque = calcularForcaDeChoque(entrada)
  const curva = curvaDeForca(entrada)
  const rPico = L + curva.pontos[curva.pontos.length - 1]!.descidaM
  const alturaNoRaio = (r: number) => A[1] - Math.sqrt(Math.max(0, r * r - dh * dh))
  const parada: VetorTabletop = [tenso[0], alturaNoRaio(rPico), tenso[2]]
  const tFrenagem = curva.duracaoS

  // --- 4. pêndulo em volta da ancoragem, no plano vertical que contém o ponto de parada;
  // o raio volta do pico ao de quem fica pendurado
  const R = L + choque.aberturaAbsorvedorM + choque.alongamentoEstaticoM
  const raioNoPendulo = (tt: number) => R + (rPico - R) * Math.exp(-tt / RETORNO_DO_ALONGAMENTO_S)
  const wx = dh > 1e-6 ? (parada[0] - A[0]) / dh : 0
  const wz = dh > 1e-6 ? (parada[2] - A[2]) / dh : 0
  const theta0 = rPico > 0 ? Math.asin(Math.min(1, dh / rPico)) : 0
  const omega = Math.sqrt(GRAVIDADE_M_S2 / R)
  const noPendulo = (tt: number): { posicao: VetorTabletop; theta: number } => {
    const theta = theta0 * Math.cos(omega * tt) * Math.exp(-AMORTECIMENTO_PENDULO * omega * tt)
    const r = raioNoPendulo(tt)
    return { posicao: [A[0] + wx * Math.sin(theta) * r, A[1] - Math.cos(theta) * r, A[2] + wz * Math.sin(theta) * r], theta }
  }

  const quadros: QuadroQueda[] = []
  const empilhar = (t: number, fase: FaseQueda, engate: VetorTabletop, forcaKN: number) => {
    quadros.push({ t, fase, engate, pes: [engate[0], engate[1] - corpo.engateAosPesM, engate[2]], forcaKN })
  }

  let impacto: ImpactoQueda | null = null
  const detectar = criarDetectorDeImpacto(cena)
  /** O último quadro bateu em algo? Guarda o impacto, com a velocidade do quadro. */
  const verificar = (fase: FaseQueda): boolean => {
    const quadro = quadros[quadros.length - 1]!
    const anterior = quadros[quadros.length - 2]
    const velocidadeMs = anterior ? dist(quadro.engate, anterior.engate) / PASSO_S : 0
    const batida = detectar(anterior?.engate ?? null, quadro.engate, corpo)
    if (!batida) return false
    impacto = { quadro: quadros.length - 1, t: quadro.t, ponto: batida.ponto, parteDoCorpo: batida.parte, alvo: batida.alvo, velocidadeMs, fase }
    return true
  }

  // um relógio só, a 30 quadros por segundo: o quadro k está em t = k/30, na fase que o instante cai
  const tFimLivre = tLivre
  const tFimFrenagem = tLivre + tFrenagem
  for (let k = 0; ; k += 1) {
    const t = k * PASSO_S
    if (t > tFimFrenagem + DURACAO_MAXIMA_PENDULO_S) break
    let fase: FaseQueda
    let engate: VetorTabletop
    let forcaKN: number
    if (t <= tFimLivre) {
      fase = 'queda-livre'
      engate = [saida[0], saida[1] - 0.5 * GRAVIDADE_M_S2 * t * t, saida[2]]
      forcaKN = 0
    } else if (t <= tFimFrenagem) {
      fase = 'frenagem'
      const ponto = curvaNoInstante(curva, t - tFimLivre)
      engate = [tenso[0], alturaNoRaio(L + ponto.descidaM), tenso[2]]
      forcaKN = ponto.forcaKN
    } else {
      const tt = t - tFimFrenagem
      const { posicao, theta } = noPendulo(tt)
      const velocidadeAngular = Math.abs(theta0) * omega * Math.exp(-AMORTECIMENTO_PENDULO * omega * tt)
      fase = (Math.abs(theta) * 180) / Math.PI < PARADO_GRAUS && velocidadeAngular < 0.02 && Math.abs(raioNoPendulo(tt) - R) < 0.005 ? 'parado' : 'pendulo'
      engate = posicao
      forcaKN = choque.pesoKN
    }
    empilhar(t, fase, engate, forcaKN)
    if (k > 0 && verificar(fase)) break
    if (fase === 'parado') break
  }

  const ultimo = quadros[quadros.length - 1]!
  const pesFinaisY = impacto ? null : ultimo.pes[1]
  return {
    ...base,
    situacao: 'ok',
    quadros,
    pontoDeSaida: saida,
    pontoTenso: tenso,
    pontoDeParada: parada,
    quedaLivreM: hLivre,
    velocidadeAoEsticarMs: vTenso,
    anguloDoPenduloGraus: (theta0 * 180) / Math.PI,
    impacto,
    pesFinaisY,
    duracaoS: ultimo.t,
    choque,
    curvaDeForca: curva,
    inicioDaFrenagemS: tLivre,
  }
}
